/* import __COLOCATED_TEMPLATE__ from './attachments.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import { type Attachment } from '@intercom/interblocks.ts';
import { type Upload, UploadScanState } from 'embercom/objects/inbox/renderable/upload';

type AnyAttachment = Upload | Attachment;

interface Args {
  attachments: AnyAttachment[];
}

interface Signature {
  Args: Args;
}

const INLINE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
const INLINE_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];

// Telegram delivers "GIFs" as muted MP4s via its Bot API `animation` field. Our
// backend stores those with contentType `video/mp4` and filename ending in `.gif`
// (see app/services/channels/telegram/helpers/media_helper.rb). Render them as
// autoplay-muted-loop video so they read like GIFs inline, matching the
// Telegram client's UX. Pre-existing behavior for non-Telegram video/mp4
// attachments (file-link fallback) is unchanged.
const TELEGRAM_ANIMATION_CONTENT_TYPE = 'video/mp4';
const TELEGRAM_ANIMATION_FILENAME_SUFFIX = '.gif';

export default class Attachments extends Component<Signature> {
  get fileAttachments(): AnyAttachment[] {
    return this.args.attachments.filter((el) => !this.isImage(el) && !this.isTelegramAnimation(el));
  }

  get images(): AnyAttachment[] {
    return this.args.attachments.filter(this.isImage);
  }

  get animations(): AnyAttachment[] {
    return this.args.attachments.filter(this.isTelegramAnimation);
  }

  private isImage(attachment: Upload | Attachment) {
    if (INLINE_IMAGE_TYPES.includes(attachment.contentType)) {
      return true;
    }
    // Android SDK sends images with 'application/force-download' despite being actual image files.
    // Fall back to filename extension check only in this case.
    if (attachment.contentType === 'application/force-download') {
      let nameLower = attachment.name.toLowerCase();
      return INLINE_IMAGE_EXTENSIONS.some((ext) => nameLower.endsWith(ext));
    }
    return false;
  }

  private isTelegramAnimation(attachment: Upload | Attachment) {
    // Only inline-render scan-clean animations. Anything flagged (infected,
    // suspicious, extension mismatch, error) must fall back to the file-link
    // renderer (Attachments::Attachment), which strips href for infected
    // files and surfaces the scan-state warning UI for the others. We must
    // not bypass that safety + UX contract.
    let scanState = 'scanState' in attachment ? attachment.scanState : undefined;
    if (scanState !== undefined && scanState !== UploadScanState.Clean) {
      return false;
    }
    return (
      attachment.contentType === TELEGRAM_ANIMATION_CONTENT_TYPE &&
      attachment.name.toLowerCase().endsWith(TELEGRAM_ANIMATION_FILENAME_SUFFIX)
    );
  }

  get hasOneAudioUpload(): boolean {
    return this.args.attachments.length === 1 && this.isAudioFile(this.args.attachments[0]);
  }

  private isAudioFile(upload: AnyAttachment): boolean {
    return upload.name.endsWith('.oga') || upload.name.endsWith('.ogg');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::PartGroups::Attachments': typeof Attachments;
    'inbox2/conversation-stream/part-groups/attachments': typeof Attachments;
  }
}
